import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";

import React from "react";
import { v4 as uuid } from "uuid";
import { useDispatch } from "react-redux";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import { lighten, Box, Grid, IconButton, Typography } from "@mui/material";

import { DVM3dView } from "./DVM3dView";
import { Thumbnail } from "./Thumbnail";
import { IoMedia3dView } from "./IoMedia3dView";
import { SwiperNavigation } from "@/shared/common/layouts/SwiperNavigation";

import { useAppStore } from "@/global/store";
import { createSlides } from "./create-slides";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { seatMapState, selectViewId3d } from "@/global/store/data/seatMap";
import { useSelectActiveEvent } from "@/shared/modules/PurchaseSeats/ChooseSeats/useSelectActiveEvent";

import type { EventType } from "@/global/store/data/types";

interface SeatMapCarouselProps {
  event?: EventType | null;
}

let swiper: SwiperClass | null = null;

export function SeatMapCarousel({ event }: SeatMapCarouselProps = {}) {
  const dispatch = useDispatch();
  const teamConfig = useTeamConfig();
  const viewId3dSelected = useAppStore(selectViewId3d);
  const { activeEvent } = useSelectActiveEvent();
  const isIoMedia3dViewEnabled =
    teamConfig?.seatMapConfigs?.isIoMedia3dViewEnabled || false;
  const suiteLanguageDisclaimer =
    teamConfig?.languages?.purchaseSeats?.suiteLanguageDisclaimer || null;

  const slides = createSlides(
    viewId3dSelected,
    isIoMedia3dViewEnabled ? IoMedia3dView : DVM3dView,
    teamConfig,
    event || activeEvent
  );
  const [_, setRenderUUID] = React.useState(uuid()); // intentionally re-renders the carousel

  const closeCarousel = () => {
    dispatch(seatMapState.actions.set({ viewId3d: "" }));
    swiper = null;
  };

  React.useEffect(() => {
    if (viewId3dSelected) {
      setRenderUUID(uuid());
      swiper?.slideToLoop?.(0);
    }
  }, [viewId3dSelected]);

  return (
    <Box>
      {/* slider */}
      <Swiper
        spaceBetween={32}
        slidesPerView={1}
        allowTouchMove={false}
        onInit={(instance) => {
          swiper = instance;
          setRenderUUID(uuid());
        }}
        onSlideChangeTransitionEnd={(instance) => {
          swiper = instance;
          setRenderUUID(uuid());
        }}
        loop={true}
        autoHeight={teamConfig?.seatMapConfigs?.isIoMedia3dViewEnabled || false}
      >
        {slides.length > 1 ? (
          <SwiperNavigation.Overlayed
            prevButton={{ offsetLeft: 8 }}
            nextButton={{ offsetRight: 8 }}
          />
        ) : null}

        {slides.map((slide, idx) => {
          return (
            <SwiperSlide key={idx}>
              {slide.type === "3d" ? <>{slide.component}</> : null}
              {slide.type === "2d" ? <>{slide.component}</> : null}
              {slide.type === "image" ? (
                <>
                  <Box position={"absolute"} top={"8px"} right={"8px"}>
                    <IconButton
                      onClick={closeCarousel}
                      size="small"
                      sx={{
                        backgroundColor: `rgba(0, 0, 0, 0.4)`,
                        color: `rgba(255, 255, 255, 0.7)`,
                        "&:hover": { backgroundColor: `rgba(0, 0, 0, 0.6)` },
                      }}
                    >
                      <CloseRoundedIcon />
                    </IconButton>
                  </Box>
                  {slide.component}
                </>
              ) : null}
            </SwiperSlide>
          );
        })}
      </Swiper>

      {suiteLanguageDisclaimer ? (
        <Box
          p={1}
          mb={2}
          mt={2}
          borderRadius={1}
          sx={{
            bgcolor: lighten(teamConfig?.colors?.primary, 0.8),
          }}
        >
          <Typography variant="caption">{suiteLanguageDisclaimer}</Typography>
        </Box>
      ) : null}

      {/* thumbnails */}
      {slides.length > 1 ? (
        <Box mt={2}>
          <Grid container spacing={2}>
            {slides.map((slide, idx) => {
              return (
                <Grid item key={idx} xs={2.3}>
                  <Thumbnail
                    slide={slide}
                    isSelected={idx === swiper?.realIndex}
                    onClick={() => {
                      swiper?.slideToLoop?.(idx);
                      setRenderUUID(uuid());
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ) : null}
    </Box>
  );
}
